import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOtpStep } from 'shared/hooks/useOtpStep';
import { useScrollToTop } from 'shared/hooks/useScrollToTop';
import { EmailStep } from './steps/EmailStep';
import { OtpStep } from './steps/OtpStep';
import { PasswordStep } from './steps/PasswordStep';
import { CognitoAuthFlowEnum, CognitoErrorCodesEnum, useAuth } from './useAuth';

enum AuthStepEnum {
  Email = 'Email',
  Otp = 'Otp',
  Password = 'Password',
  ResetPasswordConfirmation = 'ResetPasswordConfirmation',
}

type HandlerResponse = 'success' | 'failed';

export const Auth = memo(() => {
  const [step, setStep] = useState<AuthStepEnum>(AuthStepEnum.Email);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const { t } = useTranslation();
  const {
    submitEmail,
    submitPassword,
    submitOtp,
    submitResendOtp,
    signInWithGoogle,
    signInWithLinkedIn,
    resetPassword,
    confirmPasswordReset,
    loadings,
  } = useAuth();

  const { handleOtpSubmit, handleResendOtp } = useOtpStep({
    submitOtp,
    submitResendOtp,
    submittedEmail,
  });

  const handleEmailSubmit = useCallback(
    async (email: string) => {
      try {
        const flow = await submitEmail(email);
        if (flow === CognitoAuthFlowEnum.EMAIL_OTP) {
          setSubmittedEmail(email);
          setStep(AuthStepEnum.Otp);
        } else if (flow === CognitoAuthFlowEnum.USER_PASSWORD) {
          setStep(AuthStepEnum.Password);
          setSubmittedEmail(email);
        }
      } catch (err) {
        if (typeof err === 'object' && err !== null && 'message' in err) {
          const errorCode = (err as any).message;

          if (Object.values(CognitoErrorCodesEnum).includes(errorCode)) {
            throw new Error(t(`auth:cognitoErrors.${errorCode}`));
          }
        }
        throw new Error(t('auth:validationMessages.unknownError'));
      }
    },
    [submitEmail],
  );

  const handlePasswordSubmit = useCallback(
    async (password: string): Promise<HandlerResponse> => {
      if (!submittedEmail) {
        return 'failed';
      }
      try {
        const res = await submitPassword(submittedEmail, password);
        if (res !== 'success') {
          return 'failed';
        }
        return 'success';
      } catch (err) {
        if (typeof err === 'object' && err !== null && 'message' in err) {
          const errorCode = (err as any).message;

          if (Object.values(CognitoErrorCodesEnum).includes(errorCode)) {
            if (err.message === CognitoErrorCodesEnum.NotAuthorizedException) {
              throw new Error(t('auth:incorrectPassword'));
            }
            throw new Error(t(`auth:cognitoErrors.${errorCode}`));
          }
        }
        throw new Error(t('auth:validationMessages.unknownError'));
      }
    },
    [submittedEmail, submitPassword],
  );

  const handlePasswordReset = useCallback(async (): Promise<HandlerResponse> => {
    if (!submittedEmail) {
      throw new Error(t('auth:resetPasswordError'));
    }
    try {
      const res = await resetPassword(submittedEmail);
      if (res === 'success') {
        setStep(AuthStepEnum.ResetPasswordConfirmation);
        return 'success';
      }
      throw new Error(t('auth:resetPasswordError'));
    } catch (err) {
      if (err instanceof Error && err.message === t('auth:resetPasswordError')) {
        throw err;
      }
      if (typeof err === 'object' && err !== null && 'message' in err) {
        const errorCode = (err as any).message;

        if (Object.values(CognitoErrorCodesEnum).includes(errorCode)) {
          throw new Error(t(`auth:cognitoErrors.${errorCode}`));
        }
      }
      throw new Error(t('auth:validationMessages.unknownError'));
    }
  }, [submittedEmail, resetPassword]);

  const handlePasswordResetConfirm = useCallback(
    async (code: string): Promise<HandlerResponse> => {
      if (!submittedEmail) {
        return 'failed';
      }
      try {
        const res = await confirmPasswordReset(submittedEmail, code);
        if (res !== 'success') {
          throw new Error(t('auth:incorrectOtp'));
        }
        return 'success';
      } catch (err) {
        if (err instanceof Error && err.message === t('auth:incorrectOtp')) {
          throw err;
        }
        if (typeof err === 'object' && err !== null && 'message' in err) {
          const errorCode = (err as any).message;

          if (Object.values(CognitoErrorCodesEnum).includes(errorCode)) {
            throw new Error(t(`auth:cognitoErrors.${errorCode}`));
          }
        }
        throw new Error(t('auth:validationMessages.unknownError'));
      }
    },
    [submittedEmail, confirmPasswordReset],
  );

  useScrollToTop([step]);

  const stepComponent = (() => {
    switch (step) {
      case AuthStepEnum.Email:
        return (
          <EmailStep
            onLinkedInClick={signInWithLinkedIn}
            onGoogleClick={signInWithGoogle}
            onEmailSubmit={handleEmailSubmit}
            loading={
              loadings.signInWithGoogle || loadings.signInWithLinkedIn || loadings.submitEmail
            }
          />
        );
      case AuthStepEnum.Otp:
        return (
          <OtpStep
            onOtpResend={handleResendOtp}
            onOtpSubmit={handleOtpSubmit}
            loading={loadings.submitResendOtp || loadings.submitOtp}
            subtitle={t('auth:otpSentInfo', {
              email: submittedEmail,
            })}
            title={t('auth:confirmEmailWithOtpTitle')}
            className="lg:pt-24"
          />
        );
      case AuthStepEnum.Password:
        return (
          <PasswordStep
            onForgotPasswordClick={handlePasswordReset}
            onPasswordSubmit={handlePasswordSubmit}
            loading={loadings.resetPassword || loadings.submitPassword}
          />
        );
      case AuthStepEnum.ResetPasswordConfirmation:
        return (
          <OtpStep
            onOtpResend={handlePasswordReset}
            onOtpSubmit={handlePasswordResetConfirm}
            loading={loadings.confirmPasswordReset || loadings.confirmPasswordReset}
            subtitle={t('auth:otpSentInfo', {
              email: submittedEmail,
            })}
            title={t('auth:confirmEmailWithOtpTitle')}
            className="lg:pt-24"
          />
        );
      default:
        return <div />;
    }
  })();

  return (
    <div className="h-full w-full flex-col rounded-2xl bg-white px-4 py-12 lg:px-6 lg:pt-8">
      <div className="flex h-full w-full flex-col items-center">{stepComponent}</div>
    </div>
  );
});
